import React, { Fragment, useEffect, useState } from "react";
import {
  Typography,
  Stack,
  Button,
  Box,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import FocusedFlowPage from "components/FocusedFlowPage";
import StyledDataGrid from "../../../components/StyledDataGrid";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchAPI } from "utils/api";
import { BASE_CHIP_SX, STATUS_CHIP_STYLES } from "../../../constants/statusColors";
import { useGenericFormContext } from "components/GenericFormContext";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { convertNullToEmptyString } from "utils/helpers";
import { useSnackbar } from "components/snackbar/SnackbarProvider";
import { nowIso } from "../../../utils/appDateTime";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";

const ALIQUOT_NOT_CHECKED_IN_STATUS = "Not Checked-In";

// Reusable location fields
const LocationFields = ({ values = {}, onChange, showLabel = true }) => (
  <Box sx={{ display: "flex", flexWrap: "nowrap", alignItems: "center", gap: 1 }}>
    {showLabel && (
      <Typography variant="body1" sx={{ color: "text.primary", mr: 1, fontWeight: 500 }}>
        Aliquots Location:
      </Typography>
    )}
    {["freezer", "rack", "box", "position"].map((field) => (
      <TextField
        key={field}
        id={field}
        placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
        variant="outlined"
        value={values[field] || ""}
        onChange={onChange}
        sx={{ 
          width: "85px",
          "& .MuiOutlinedInput-root": { height: "32px" }
        }}
      />
    ))}
  </Box>
);

const GridInputCell = ({ id, value, onChange }) => (
  <TextField
    id={id}
    size="small"
    variant="standard"
    fullWidth
    value={value || ""}
    onChange={onChange}
    InputProps={{ disableUnderline: true }}
    sx={{
      bgcolor: "rgba(0, 0, 0, 0.04)",
      borderRadius: "4px",
      "& .MuiInput-root::before": { borderBottom: "none !important" },
      "& .MuiInput-root::after": { borderBottom: "none !important" },
      "& .MuiInput-root:hover:not(.Mui-disabled):before": { borderBottom: "none !important" },
      "& input": { textAlign: "center" },
      "&:hover": { bgcolor: "rgba(0, 0, 0, 0.08)" },
      "& .Mui-focused": { outline: "2px solid maroon", bgcolor: "transparent", borderRadius: "4px" }
    }}
  />
);

const CheckInAliquots = () => {
  const [lookingUpAliquots, setLookingUpAliquots] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [aliquots, setAliquots] = useState([]);

  // global state
  const [allSameLocation, setAllSameLocation] = useState("");
  const [globalLocation, setGlobalLocation] = useState({});

  // apply row state per type
  const [applyRows, setApplyRows] = useState({});

  const { formData, setFormData } = useGenericFormContext();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = location.state?.returnTo || BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_CHECK_IN;
  const { openSnackbar } = useSnackbar();

  useEffect(() => {
    if (location.state?.barcode && formData.barcode !== location.state.barcode) {
      setFormData({ ...formData, barcode: location.state.barcode });
    }
  }, [formData, setFormData, location.state?.barcode]);

  /* FETCH ALIQUOTS */
  useEffect(() => {
    const fetchData = async () => {
      if (!formData.barcode || formData.barcode.length !== 7) {
        setPreviewData(null);
        setAliquots([]);
        return;
      }

      setLookingUpAliquots(true);
      try {
        const data = await fetchAPI(
          `biobank/aliquots/barcode/${formData.barcode}?status=${encodeURIComponent(ALIQUOT_NOT_CHECKED_IN_STATUS)}`
        );
        setPreviewData(data);
        
        const flatAliquots = (data.samples || []).flatMap(s => 
          (s.aliquots || []).map(a => {
            const isStr = typeof a === "string";
            const actualId = isStr ? a : (a.aliquotId || a.id);
            return {
              ...(isStr ? {} : a),
              sampleType: s.sampleType,
              aliquotId: actualId,
              id: actualId || Math.random().toString(),
            };
          })
        );
        setAliquots(flatAliquots);
        
      } catch (e) {
        setPreviewData(null);
        setAliquots([]);
      } finally {
        setLookingUpAliquots(false);
      }
    };

    fetchData();
  }, [formData.barcode]);

  /* INDIVIDUAL CHANGE */
  const handleValueChange = (event, rowId) => {
    const { id, value } = event.target;
    setAliquots((prev) =>
      prev.map((a) => ((a.aliquotId || a.id) === rowId ? { ...a, [id]: value } : a)),
    );
  };

  /* GLOBAL LOCATION */
  const handleGlobalChange = (event) => {
    const { id, value } = event.target;
    setGlobalLocation((prev) => ({ ...prev, [id]: value }));
    setAliquots((prev) => prev.map((a) => ({ ...a, [id]: value })));
  };

  /* APPLY ROW CHANGE */
  const handleApplyRowChange = (type, event) => {
    const { id, value } = event.target;
    setApplyRows((prev) => ({
      ...prev,
      [type]: {
        ...(prev[type] || {}),
        [id]: value,
      },
    }));
  };

  const handleApplyToAll = (type) => {
    const applyData = applyRows[type] || {};
    setAliquots((prev) =>
      prev.map((a) => {
        if (a.sampleType === type) {
          return { ...a, ...applyData };
        }
        return a;
      }),
    );
  };

  const handleApplyToEmpty = (type) => {
    const applyData = applyRows[type] || {};
    setAliquots((prev) =>
      prev.map((a) => {
        if (a.sampleType === type) {
          return {
            ...a,
            freezer: a.freezer || applyData.freezer,
            rack: a.rack || applyData.rack,
            box: a.box || applyData.box,
            position: a.position || applyData.position,
          };
        }
        return a;
      }),
    );
  };

  /* ALL SAME LOCATION CHANGE */
  const handleAllSameLocationChange = (val) => {
    setAllSameLocation(val);
    if (val === "yes") {
      if (Object.keys(globalLocation).length > 0) {
        setAliquots((prev) => prev.map((a) => ({ ...a, ...globalLocation })));
      }
    } else if (val === "no") {
      const newApplyRows = {};
      const sortedTypes = previewData?.samples?.map(s => s.sampleType) || [];
      sortedTypes.forEach(t => {
        newApplyRows[t] = { ...globalLocation };
      });
      setApplyRows(newApplyRows);
    }
  };

  /* PARTIAL CHECK-IN LOGIC */
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const isValidAliquot = (a) => a.freezer && a.rack && a.box && a.position;
  const isDirtyAliquot = (a) => !isValidAliquot(a) && (a.freezer || a.rack || a.box || a.position);
  
  const readyAliquots = aliquots.filter(isValidAliquot);
  const dirtyAliquots = aliquots.filter(isDirtyAliquot);
  const canSubmit = readyAliquots.length > 0;

  /* SAVE */
  const handleContinue = async () => {
    if (!canSubmit) {
      openSnackbar("No aliquots ready for check-in", "error");
      return;
    }

    if (dirtyAliquots.length > 0) {
      setConfirmDialogOpen(true);
      return;
    }

    executeCheckIn();
  };

  const executeCheckIn = async () => {
    setConfirmDialogOpen(false);
    try {
      const payload = {
        items: readyAliquots.map((a) => ({
          aliquotId: a.aliquotId,
          freezer: a.freezer,
          rack: a.rack,
          box: a.box,
          position: Number(a.position) || a.position,
          checkInDateTime: nowIso(),
        })),
      };

      const response = await fetchAPI(
        "biobank/aliquots/check-in/batch",
        "POST",
        {},
        payload
      );

      if (response && response.failedAliquots && response.failedAliquots.length > 0) {
        openSnackbar(`Checked-in ${response.completedCount} aliquots. Failed: ${response.failedAliquots.length}`, "warning");
        if (response.completedCount > 0) {
          navigate(returnTo);
        }
      } else {
        const count = response?.completedCount ?? readyAliquots.length;
        openSnackbar(`${count} aliquots checked-in successfully`, "success");
        navigate(returnTo);
      }
    } catch {
      openSnackbar("Error checking in aliquots", "error");
    }
  };

  const validAliquotId = !!previewData && aliquots.length > 0;

  return (
    <FocusedFlowPage
      title="Check-In New Aliquots"
      onClose={() => navigate(returnTo)}
      maxWidth={1000}
      actions={[
        <Button key="back" variant="outlined" onClick={() => navigate(returnTo)}>
          Back
        </Button>,
        <Button
          key="submit"
          variant="contained"
          onClick={handleContinue}
          disabled={!canSubmit}
          sx={{ minWidth: 200 }}
        >
          {readyAliquots.length === aliquots.length && aliquots.length > 0
            ? "Complete Check-In"
            : readyAliquots.length > 0
              ? `Check-In ${readyAliquots.length} Aliquots`
              : "Complete Check-In"}
        </Button>,
      ]}
    >
      {!lookingUpAliquots && validAliquotId && previewData && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1, mr: 1 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ lineHeight: 1.2 }}>
              Barcode ID: {formData.barcode}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
              {previewData.aliquotCount} aliquots across type {previewData.samples.map(s => s.sampleType).join(", ")}
            </Typography>
          </Box>
        </Box>
      )}

      <Stack spacing={4} sx={{ mt: 4 }}>
        {lookingUpAliquots && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress />
          </Box>
        )}

        {/* GLOBAL QUESTION */}
        {!lookingUpAliquots && validAliquotId && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 1, flexWrap: "wrap" }}>
              <Typography variant="subtitle1" fontWeight="bold">
                All aliquots in the same location?
              </Typography>
              <RadioGroup
                row
                value={allSameLocation}
                onChange={(e) => handleAllSameLocationChange(e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>

              {/* YES BRANCH (Inline) */}
              {allSameLocation === "yes" && (
                <Box sx={{ ml: "auto" }}>
                  <LocationFields
                    values={globalLocation}
                    onChange={handleGlobalChange}
                    showLabel={false}
                  />
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* NO BRANCH: PER SAMPLE TYPE FLOW */}
        {!lookingUpAliquots && validAliquotId && allSameLocation === "no" && previewData && (
          <Stack spacing={6}>
            {previewData.samples.map((s) => {
              const type = s.sampleType;
              const typeAliquots = aliquots.filter(a => a.sampleType === type);
              const readyCount = typeAliquots.filter(isValidAliquot).length;
              const totalCount = typeAliquots.length;
              const isDone = readyCount === totalCount && totalCount > 0;
              const isPending = readyCount === 0;
              const chipLabel = isDone ? "Done" : isPending ? "Pending" : `${readyCount}/${totalCount} Ready`;
              const chipStyle = isDone ? STATUS_CHIP_STYLES.success : isPending ? STATUS_CHIP_STYLES.warning : STATUS_CHIP_STYLES.info;

              return (
                <Box key={type}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      ▍Type {type}
                    </Typography>
                    <Chip
                      label={chipLabel}
                      size="small"
                      sx={{ ...BASE_CHIP_SX, ...chipStyle, fontWeight: "bold" }}
                    />
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", mb: 2, gap: 2 }}>
                    <LocationFields
                      values={applyRows[type] || {}}
                      onChange={(e) => handleApplyRowChange(type, e)}
                    />
                    <Box sx={{ display: "flex", gap: 1, ml: "auto" }}>
                      <Button variant="outlined" size="small" onClick={() => handleApplyToEmpty(type)}>
                        Apply to empty
                      </Button>
                      <Button variant="outlined" size="small" onClick={() => handleApplyToAll(type)}>
                        Apply to all
                      </Button>
                    </Box>
                  </Box>

                  <Box sx={{ width: "100%", height: 300 }}>
                    <StyledDataGrid
                      rows={typeAliquots}
                      columns={[
                        { field: "id", headerName: "Aliquot ID", flex: 1 },
                        {
                          field: "freezer",
                          headerName: "Freezer",
                          flex: 1,
                          renderCell: (params) => (
                            <GridInputCell id="freezer" value={params.row.freezer} onChange={(e) => handleValueChange(e, params.row.id)} />
                          )
                        },
                        {
                          field: "rack",
                          headerName: "Rack",
                          flex: 1,
                          renderCell: (params) => (
                            <GridInputCell id="rack" value={params.row.rack} onChange={(e) => handleValueChange(e, params.row.id)} />
                          )
                        },
                        {
                          field: "box",
                          headerName: "Box",
                          flex: 1,
                          renderCell: (params) => (
                            <GridInputCell id="box" value={params.row.box} onChange={(e) => handleValueChange(e, params.row.id)} />
                          )
                        },
                        {
                          field: "position",
                          headerName: "Position",
                          flex: 1,
                          renderCell: (params) => (
                            <GridInputCell id="position" value={params.row.position} onChange={(e) => handleValueChange(e, params.row.id)} />
                          )
                        }
                      ]}
                      hideFooter
                      disableColumnMenu
                      disableColumnSorting
                    />
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>

      {/* Dirty Rows Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Incomplete Data Detected</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have <b>{dirtyAliquots.length}</b> aliquot(s) with incomplete location data (some fields are filled but not all).
            <br /><br />
            These aliquots will <b>not</b> be checked in. Are you sure you want to proceed and check in the <b>{readyAliquots.length}</b> ready aliquot(s)?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={executeCheckIn} variant="contained" color="primary">
            Proceed Anyway
          </Button>
        </DialogActions>
      </Dialog>
    </FocusedFlowPage>
  );
};

export default CheckInAliquots;
